"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";

type DataSaverConnection = EventTarget & {
  saveData?: boolean;
  effectiveType?: string;
  downlink?: number;
};

type MotionNavigator = Navigator & {
  connection?: DataSaverConnection;
  deviceMemory?: number;
};

type IndustryImmersiveHeroMediaProps = {
  allowManualPlayback?: boolean;
  webmSources?: boolean;
  height: number;
  industryId: string;
  objectPosition?: string;
  pauseLabel: string;
  playLabel: string;
  posterBase: string;
  width: number;
};

export function IndustryImmersiveHeroMedia({
  allowManualPlayback = false,
  webmSources = false,
  height,
  industryId,
  objectPosition = "center",
  pauseLabel,
  playLabel,
  posterBase,
  width,
}: IndustryImmersiveHeroMediaProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const userPausedRef = useRef(false);
  const userRequestedVideoRef = useRef(false);
  const inViewRef = useRef(true);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isPlayable, setIsPlayable] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [videoPolicy, setVideoPolicy] = useState("pending");
  const videoSource = `/assets/videos/industries/${industryId}-loop.mp4`;
  const mobileVideoSource = `/assets/videos/industries/${industryId}-mobile.mp4`;

  const syncPlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const shouldPlay =
      shouldLoadVideo &&
      !hasFailed &&
      !userPausedRef.current &&
      inViewRef.current &&
      !document.hidden;

    if (shouldPlay && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      void video
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
      return;
    }

    video.pause();
    setIsPlaying(false);
  }, [hasFailed, shouldLoadVideo]);

  useEffect(() => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    const motionNavigator = navigator as MotionNavigator;
    const connection = motionNavigator.connection;

    const updateEligibility = () => {
      const slowConnection =
        ["slow-2g", "2g", "3g"].includes(connection?.effectiveType ?? "") ||
        (typeof connection?.downlink === "number" && connection.downlink < 1.5);
      const lowMemoryDevice =
        typeof motionNavigator.deviceMemory === "number" &&
        motionNavigator.deviceMemory < 3;
      const eligible =
        !reducedMotion.matches &&
        (userRequestedVideoRef.current ||
          (!connection?.saveData && !slowConnection && !lowMemoryDevice));
      setVideoPolicy(
        reducedMotion.matches ? "reduced-motion"
          : userRequestedVideoRef.current ? "manual-playback"
          : connection?.saveData ? "save-data"
          : slowConnection ? "slow-connection"
          : lowMemoryDevice ? "low-memory" : "autoplay",
      );
      setShouldLoadVideo(eligible);
      if (!eligible) {
        videoRef.current?.pause();
        setIsPlaying(false);
        setIsPlayable(false);
        setIsReady(false);
      }
    };

    updateEligibility();
    reducedMotion.addEventListener("change", updateEligibility);
    connection?.addEventListener("change", updateEligibility);

    return () => {
      reducedMotion.removeEventListener("change", updateEligibility);
      connection?.removeEventListener("change", updateEligibility);
    };
  }, []);

  useEffect(() => {
    if (!shouldLoadVideo || hasFailed) return;
    const root = rootRef.current;
    const video = videoRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        inViewRef.current = entry?.isIntersecting ?? true;
        syncPlayback();
      },
      { threshold: 0.16 },
    );
    const handleVisibilityChange = () => syncPlayback();

    observer.observe(root);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    syncPlayback();

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      video?.pause();
    };
  }, [hasFailed, shouldLoadVideo, syncPlayback]);

  const handleToggle = () => {
    const video = videoRef.current;
    if (!video && allowManualPlayback && videoPolicy !== "reduced-motion") {
      userRequestedVideoRef.current = true;
      setVideoPolicy("manual-playback");
      setShouldLoadVideo(true);
      return;
    }
    if (!video) return;

    if (video.paused) {
      userPausedRef.current = false;
      syncPlayback();
      return;
    }

    userPausedRef.current = true;
    video.pause();
    setIsPlaying(false);
  };

  return (
    <div
      className="mining-immersive-hero__media industry-immersive-hero__media"
      data-motion-media="licensed-cinematic-video"
      data-motion-playing={isPlaying ? "true" : "false"}
      data-motion-variant={industryId}
      data-video-ready={isReady && !hasFailed ? "true" : "false"}
      data-video-eligible={shouldLoadVideo ? "true" : "false"}
      data-video-failed={hasFailed ? "true" : "false"}
      data-video-policy={videoPolicy}
      ref={rootRef}
      style={
        {
          "--industry-motion-object-position": objectPosition,
        } as CSSProperties
      }
    >
      <picture className="mining-immersive-hero__poster">
        <source srcSet={`${posterBase}.avif`} type="image/avif" />
        <source srcSet={`${posterBase}.webp`} type="image/webp" />
        <img
          src={`${posterBase}.jpg`}
          alt=""
          width={width}
          height={height}
          fetchPriority="high"
        />
      </picture>

      {shouldLoadVideo && !hasFailed ? (
        <video
          ref={videoRef}
          className="mining-immersive-hero__video"
          aria-hidden="true"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          onCanPlay={() => {
            setIsPlayable(true);
            syncPlayback();
          }}
          onError={(event) => {
            // A rejected <source> must not prevent the browser trying the next format.
            if (event.target !== event.currentTarget || !event.currentTarget.error) return;
            setHasFailed(true);
            setIsPlayable(false);
            setIsReady(false);
            setIsPlaying(false);
          }}
          onPause={() => setIsPlaying(false)}
          onPlay={() => {
            setIsPlaying(true);
            setIsReady(true);
          }}
        >
          {webmSources ? <>
            <source
              media="(max-width: 920px)"
              src={`/assets/videos/industries/${industryId}-mobile.webm`}
              type="video/webm"
            />
            <source
              src={`/assets/videos/industries/${industryId}-loop.webm`}
              type="video/webm"
            />
          </> : null}
          <source
            media="(max-width: 920px)"
            src={mobileVideoSource}
            type="video/mp4"
          />
          <source src={videoSource} type="video/mp4" />
        </video>
      ) : null}

      {!hasFailed && ((shouldLoadVideo && isPlayable) ||
        (allowManualPlayback && !shouldLoadVideo && videoPolicy !== "pending" && videoPolicy !== "reduced-motion")) ? (
        <button
          className="mining-immersive-hero__motion-control"
          type="button"
          onClick={handleToggle}
          aria-label={isPlaying ? pauseLabel : playLabel}
        >
          {isPlaying ? (
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M7 5h3v14H7zM14 5h3v14h-3z" />
            </svg>
          ) : (
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="m8 5 11 7-11 7z" />
            </svg>
          )}
          <span>{isPlaying ? pauseLabel : playLabel}</span>
        </button>
      ) : null}
    </div>
  );
}
